/**
 * API + Patch Strategy
 * Fetches from API and automatically patches the first result
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseDataFetchStrategy } from './data-fetch-strategy.interface';
import { IDataFetchContext } from '../models';
import { DataFetchType } from '../constants';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiPatchStrategy extends BaseDataFetchStrategy {
  constructor(private http: HttpClient) {
    super();
  }
  
  canHandle(dataFetchType: string): boolean {
    return dataFetchType?.toLowerCase() === DataFetchType.APInPatch;
  }
  
  getName(): string {
    return 'API+Patch Strategy';
  }
  
  override validate(context: IDataFetchContext): boolean {
    if (!super.validate(context)) {
      return false;
    }
    
    if (!context.field.dataFetchFrom) {
      this.logWarning('No API endpoint specified', context);
      return false;
    }
    
    if (!context.field.patchDataInto) {
      this.logWarning('No patchDataInto configuration specified', context);
      return false;
    }
    
    // If field has dependency, check if dependent value exists
    if (context.field.dependentOn && !this.hasDependentValue(context)) {
      return false;
    }
    
    return true;
  }
  
  execute(context: IDataFetchContext): Observable<any> {
    if (!this.validate(context)) {
      return of(null);
    }
    
    try {
      // Build the API URL - just like legacy formatAPIURL
      let apiUrl = context.field.dataFetchFrom!;
      
      // Replace placeholders (legacy behavior)
      apiUrl = this.replacePlaceholders(apiUrl, context);
      
      // Add base URL if not absolute
      if (!apiUrl.startsWith('http')) {
        apiUrl = environment.getApiUrl(apiUrl);
      }
      
      // ✅ LEGACY BEHAVIOR: The URL template defines ALL parameters
      // Don't add extra query parameters - just use the URL as-is after placeholder replacement
      
      this.logInfo(`Fetching and patching from API: ${apiUrl}`, context);
      
      // Make API call without adding extra params
      return this.http.get(apiUrl).pipe(
        map((response: any) => {
          const data = this.normalizeResponse(response);
          
          // Get first result
          const firstResult = Array.isArray(data) && data.length > 0 ? data[0] : data;
          
          if (firstResult) {
            // Patch values into other fields
            this.patchValues(context, firstResult);
          }
          
          return firstResult;
        }),
        catchError((error) => {
          this.logError(error, context);
          return of(null);
        })
      );
      
    } catch (error) {
      this.logError(error, context);
      return of(null);
    }
  }
  
  /**
   * Patch values from API response into form fields
   * Format: "targetField1:sourceProperty1,targetField2:sourceProperty2"
   */
  private patchValues(context: IDataFetchContext, data: any): void {
    if (!context.field.patchDataInto || !data) return;
    
    const mappings = context.field.patchDataInto.split(',').map(m => m.trim());
    
    mappings.forEach(mapping => {
      const [targetField, sourceProperty] = mapping.split(':').map(s => s.trim());
      
      if (targetField && sourceProperty && data[sourceProperty] !== undefined) {
        const targetControl = context.formGroup.get(targetField);
        if (targetControl) {
          targetControl.setValue(data[sourceProperty], { emitEvent: false });
          this.logInfo(`Patched ${sourceProperty} → ${targetField}: ${data[sourceProperty]}`, context);
        }
      }
    });
  }
  
  /**
   * Build HTTP query parameters
   */
  private buildQueryParams(context: IDataFetchContext): HttpParams {
    let params = new HttpParams();
    
    if (context.companyId) {
      params = params.set('CompanyId', context.companyId.toString());
    }
    
    if (context.branchId) {
      params = params.set('BranchId', context.branchId.toString());
    }
    
    if (context.searchTerm) {
      params = params.set('searchTerm', context.searchTerm);
    }
    
    if (context.masterId) {
      params = params.set('masterId', context.masterId.toString());
    }
    
    // Add dependent field value
    if (context.field.dependentOn) {
      const dependentValue = this.getDependentValue(context);
      if (dependentValue !== null && dependentValue !== undefined) {
        const paramValue = typeof dependentValue === 'object'
          ? dependentValue[context.field.displayPropertyName || 'id'] || dependentValue.id || ''
          : dependentValue;
        params = params.set('DependentOnId', paramValue.toString());
      }
    }
    
    return params;
  }
  
  /**
   * Normalize API response
   */
  private normalizeResponse(response: any): any {
    if (Array.isArray(response)) {
      return response;
    }
    
    if (response.data) {
      return response.data;
    }
    
    if (response.result) {
      return response.result;
    }
    
    return response;
  }
}
